<template>
  <div>
    <v-card
      :to="
        link
          ? {
              name: 'member-management-deadline-monitoring-deadline-details',
              params: {
                itemId: deadline.meta.id,
              },
            }
          : null
      "
      :outlined="outlined"
    >
      <v-row no-gutters>
        <v-col cols="9">
          <v-card-title v-if="!hideTitle" class="subheading">
            {{ deadline.title }}
          </v-card-title>

          <v-card-text>
            <v-row dense class="match-height">
              <v-col
                v-for="(watcher, index) in deadline.watchers"
                :key="index"
                cols="sm"
              >
                <v-card
                  class="pa-2"
                  :color="`${getMaterialColor(watcher.color, {
                    input: 'hex',
                    output: 'word',
                  })} lighten-4`"
                  dark
                  :disabled="watcher.value == 0"
                  outlined
                >
                  <div
                    :class="[
                      'font-weight-medium text-h5 text-center text--darken-4',
                      `${getMaterialColor(watcher.color, {
                        input: 'hex',
                        output: 'word',
                      })}--text`,
                    ]"
                  >
                    {{ watcher.value }}
                  </div>
                  <div
                    :class="[
                      'font-weight-medium subtitle-2 text-center text--darken-4',
                      `${getMaterialColor(watcher.color, {
                        input: 'hex',
                        output: 'word',
                      })}--text`,
                    ]"
                  >
                    {{ watcher.title }}
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-col>
        <v-divider vertical></v-divider>
        <v-col cols="3" class="d-flex justify-center align-center">
          <VueApexCharts
            type="donut"
            :options="timeSpendingChartConfig"
            :series="timeSpendingChartSeries"
            height="95px"
            class="mx-2 px-2"
          />
        </v-col>
      </v-row>
      <v-divider v-if="showDeadlineDistribution"></v-divider>
      <VueApexCharts
        v-if="showDeadlineDistribution"
        type="heatmap"
        :options="heatmapChartConfig"
        :series="heatmapChartSeries"
        :height="300"
        class="mx-2 px-2 py-3"
      />
      <v-divider v-if="showActions"></v-divider>
      <v-card-actions v-if="showActions" class="mx-2">
        <v-btn
          depressed
          outlined
          text
          :to="{
            name: 'member-management-deadline-monitoring-deadline-bulk-input',
            params: {
              organizationId: $route.params.organizationId,
              itemId: deadline.meta.id,
            },
          }"
          ><v-icon left>mdi-form-textbox</v-icon>Sammeleingabe</v-btn
        >
        <v-btn
          depressed
          outlined
          text
          :to="{
            name: 'member-management-deadline-monitoring-deadline-edit',
            params: {
              organizationId: $route.params.organizationId,
              itemId: deadline.meta.id,
            },
          }"
          ><v-icon left>mdi-pencil</v-icon>Bearbeiten</v-btn
        >
        <v-spacer></v-spacer>
        <v-btn
          depressed
          outlined
          text
          :to="{
            name: 'member-management-deadline-monitoring-deadline-details',
            params: {
              organizationId: $route.params.organizationId,
              itemId: deadline.meta.id,
            },
          }"
          ><v-icon left>mdi-eye</v-icon>Details</v-btn
        >
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import { getMaterialColor } from "@/util/styles";
export default {
  name: "deadline-detail-counter-card",
  components: { VueApexCharts },
  props: {
    deadline: {
      type: Object,
      required: true,
    },
    deadlineEntries: {
      type: Array,
      default: () => [],
      required: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    link: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
    showDeadlineDistribution: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    timeSpendingChartData() {
      // Prepare labels, colors, and series in one place
      const watchers = this.deadline.watchers || [];
      return {
        labels: watchers.map((w) => w.title),
        colors: watchers.map((w) =>
          this.getMaterialColor(w.color, { output: "hex", type: "lighten-4" })
        ),
        series: watchers.map((w) => w.value),
      };
    },
    timeSpendingChartConfig() {
      return {
        chart: {
          height: 157,
          width: 130,
          parentHeightOffset: 0,
          type: "donut",
          sparkline: { enabled: true },
        },
        labels: this.timeSpendingChartData.labels,
        colors: this.timeSpendingChartData.colors,
        stroke: { width: 0 },
        dataLabels: {
          enabled: false,
        },
        legend: { show: false },
        tooltip: { theme: false },
        grid: { padding: { top: 0 } },
        plotOptions: {
          pie: {
            donut: {
              size: "70%",
              labels: {
                show: true,
                value: {
                  fontSize: "1.125rem",
                  color:
                    "rgba(var(--v-theme-on-background), var(--v-high-emphasis-opacity))",
                  fontWeight: 500,
                  offsetY: -15,
                  formatter: function (val, opts) {
                    const total = opts.globals.seriesTotals.reduce(
                      (a, b) => a + b,
                      0
                    );
                    const percentage = Math.round((val / total) * 100);
                    return `${percentage}%`;
                  },
                },
                name: { offsetY: 20 },
                total: {
                  show: true,
                  fontSize: ".7rem",
                  label: "Gesamt",
                  color:
                    "rgba(var(--v-theme-on-background), var(--v-disabled-opacity))",
                  formatter: function (w) {
                    return w.globals.seriesTotals.reduce((a, b) => {
                      return a + b;
                    }, 0);
                  },
                },
              },
            },
          },
        },
      };
    },
    timeSpendingChartSeries() {
      return this.timeSpendingChartData.series;
    },
  },
  data() {
    return {
      // heatmap
      // Heatmap chart configuration
      heatmapChartConfig: {
        grid: {
          show: false,
        },
        chart: {
          type: "heatmap",
          height: 250,
          zoom: {
            enabled: false,
          },
        },
        title: {
          text: `Verteilung der Fristenabläufe in ${new Date().getFullYear()}`,
        },
        plotOptions: {
          heatmap: {
            radius: 4,
            enableShades: true,
            shadeIntensity: 0.2,
            distributed: false,
            useFillColorAsStroke: false,
            colorScale: {
              ranges: [
                {
                  from: 0,
                  to: 0,
                  name: "Keine Fristenbläufe",
                  color: "#4CAF50",
                },
                {
                  from: 1,
                  to: 1000,
                  name: "Fristenabläufe an diesem Tag",
                  color: "#F44336",
                },
                {
                  from: -1,
                  to: -1,
                  name: "Tag existiert nicht",
                  color: "#000",
                },
              ],
            },
          },
        },
        stroke: {
          width: 4,
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          type: "category",
          title: {
            text: "Tage",
          },
        },
        yaxis: {
          title: {
            text: "Monate",
          },
        },
        tooltip: {
          theme: this.$vuetify.theme.dark ? "dark" : "light",
          style: {
            fontFamily: "Roboto",
          },
          x: {
            formatter: (val, opts) => {
              const seriesIndex = opts.seriesIndex;
              const year = new Date().getFullYear();
              const month = seriesIndex;
              const day = val;
              const dayStr = String(day).padStart(2, "0");
              const monthStr = String(month + 1).padStart(2, "0");
              return `${dayStr}.${monthStr}.${year}`;
            },
          },
          y: {
            formatter: (val, opts) => {
              const seriesIndex = opts.seriesIndex;
              const dataPointIndex = opts.dataPointIndex;
              const year = new Date().getFullYear();
              const month = seriesIndex;
              // Get the day from the data's x property
              const day =
                opts.w.config.series[seriesIndex].data[dataPointIndex].x;
              const dayStr = String(day).padStart(2, "0");
              const monthStr = String(month + 1).padStart(2, "0");
              const deadlineText = val === 1 ? "Frist" : "Fristen";
              if (val === -1) {
                return "Nicht existierender Tag";
              }
              return `${val} ${deadlineText} laufen am ${dayStr}.${monthStr}.${year} ab`;
            },
          },
        },
      },
      heatmapChartSeries: [
        // todo
      ],
    };
  },
  created() {
    this.generateHeatmapSeries();
  },
  methods: {
    getMaterialColor(color, options = {}) {
      return getMaterialColor(color, options);
    },
    generateHeatmapSeries() {
      // Helper to get days in month
      function daysInMonth(year, month) {
        return new Date(year, month + 1, 0).getDate();
      }

      const year = new Date().getFullYear();
      // Initialize a 2D array for months and days (1-31)
      const heatmapData = Array.from({ length: 12 }, (_, month) => {
        const days = Array(31).fill(0);
        // Mark non-existing days as -1
        const dim = daysInMonth(year, month);
        for (let d = dim; d < 31; d++) {
          days[d] = -1;
        }
        return days;
      });

      // Populate heatmapData with deadlineEntries, filter out null dueDate
      this.deadlineEntries
        .filter((entry) => entry.dueDate)
        .forEach((entry) => {
          const dueDate = entry.dueDate.toDate(); // Firestore timestamp to JS Date
          const month = dueDate.getMonth(); // 0-based index for months
          const day = dueDate.getDate() - 1; // 1-based index for days
          if (heatmapData[month] && heatmapData[month][day] !== -1) {
            heatmapData[month][day]++;
          }
        });

      // Transform heatmapData into ApexCharts series format
      this.heatmapChartSeries = heatmapData.map((monthData, monthIndex) => ({
        name: new Date(year, monthIndex, 1).toLocaleString("default", {
          month: "long",
        }),
        data: monthData.map((value, dayIndex) => ({
          x: dayIndex + 1,
          y: value,
        })),
      }));
    },
  },
};
</script>
